import ApiService from './ApiService'
import { buildURL } from 'utils/buildUrl'
import dayjs from 'dayjs'

const endpoint = '/consultations'

const normalizeAppointmentQuery = (data = {}) => {
    const query = { ...data }

    delete query.silent

    if (query.selectedDate) {
        query.selectedDate = dayjs(query.selectedDate).format('YYYY-MM-DD')
    } else {
        delete query.selectedDate
    }

    return query
}

export async function apiCreateObservation(data, id) {
    return ApiService.fetchData({
        url: `${endpoint}/observation/${id}`,
        method: 'post',
        data,
    })
}

export async function apiUploadDocument(data, id) {
    return ApiService.fetchData({
        url: `${endpoint}/documents/${id}`,
        method: 'post',
        data,
    })
}

export async function apiEditAppointement(id, data) {
    const url = buildURL(endpoint, [id], {})

    return ApiService.fetchData({
        url,
        method: 'patch',
        data,
    })
}

export async function apiConfirmAppointment(id) {
    return ApiService.fetchData({
        url: buildURL(endpoint, [id, 'confirm'], {}),
        method: 'patch',
    })
}

export async function apiUpdateConsultationCompletionChecklist(id, data) {
    const url = buildURL(endpoint, [id, 'completion-checklist'], {})

    return ApiService.fetchData({
        url,
        method: 'patch',
        data,
    })
}

export async function apiUpdateAppointmentStatus(id, data) {
    const url = buildURL(endpoint, [id, 'appointment-status'], {})

    return ApiService.fetchData({
        url,
        method: 'patch',
        data,
    })
}

export async function apiUpdateConsultationFollowUpDelays(id, data) {
    const url = buildURL(endpoint, [id, 'follow-up-delays'], {})

    return ApiService.fetchData({
        url,
        method: 'put',
        data,
    })
}

export async function apiCreateAppointement(data) {
    return ApiService.fetchData({
        url: `${endpoint}`,
        method: 'post',
        data,
    })
}

export async function apiGetAppointements(data) {
    const url = buildURL(endpoint, [], normalizeAppointmentQuery(data))
    return ApiService.fetchData({
        url,
        method: 'get',
        data,
    })
}

export async function apiGetPatientNextAppointment(
    patientId,
    { excludeConsultationId } = {}
) {
    const query = excludeConsultationId ? { excludeConsultationId } : {}
    const url = buildURL(
        endpoint,
        ['patients', patientId, 'next-appointment'],
        query
    )

    return ApiService.fetchData({
        url,
        method: 'get',
    })
}

export async function apiGetObservations(id) {
    return ApiService.fetchData({
        url: `${endpoint}/observation/${id}`,
        method: 'get',
    })
}

export async function apiGetDocuments(id) {
    return ApiService.fetchData({
        url: `${endpoint}/documents/${id}`,
        method: 'get',
    })
}

export async function apiGetTreatments(id) {
    return ApiService.fetchData({
        url: `${endpoint}/treatments/${id}`,
        method: 'get',
    })
}

export async function apiGetAppointement(id) {
    const url = buildURL(endpoint, [id], {})
    return ApiService.fetchData({
        url,
        method: 'get',
    })
}

export async function apiGetConsultationsHistory({ data, queryParams = {} }) {
    const queryString = Object.entries(queryParams)
        .map(
            ([key, value]) =>
                `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
        .join('&')
    // Add the query string to the URL if it's not empty
    const url = queryString
        ? `${endpoint}/treatments-history/${data}?${queryString}`
        : endpoint
    return ApiService.fetchData({
        url: url,
        method: 'get',
    })
}

export async function apiGetConsultationsHistoryData({
    data,
    queryParams = {},
}) {
    const queryString = Object.entries(queryParams)
        .map(
            ([key, value]) =>
                `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
        .join('&')
    // Add the query string to the URL if it's not empty
    const url = `${endpoint}/treatments-history-stats/${data}?${queryString}`
    return ApiService.fetchData({
        url: url,
        method: 'get',
    })
}

export async function apiGetAvailableTreatements({ data, queryParams = {} }) {
    const queryString = Object.entries(queryParams)
        .map(
            ([key, value]) =>
                `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
        .join('&')
    // Add the query string to the URL if it's not empty
    const url = queryString
        ? `${endpoint}/available-treatments/${data.consultationTime}?${queryString}`
        : endpoint
    return ApiService.fetchData({
        url: url,
        method: 'get',
        data,
    })
}

export async function apiGetAvailableAssistants({ data, queryParams = {} }) {
    const queryString = Object.entries(queryParams)
        .map(
            ([key, value]) =>
                `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
        .join('&')

    // Add the query string to the URL if it's not empty
    const url = queryString
        ? `${endpoint}/available-assistants/${data.consultationTime}?${queryString}`
        : endpoint
    return ApiService.fetchData({
        url: url,
        method: 'get',
        data,
    })
}

export async function apiDeleteAppointment(id, queryParams = {}) {
    const url = buildURL(endpoint, [id], queryParams)

    return ApiService.fetchData({
        url,
        method: 'delete',
        id,
    })
}

export async function apiDeleteOldAppointment(id, queryParams = {}) {
    const url = buildURL(`${endpoint}/old-appointment`, [id], queryParams)
    return ApiService.fetchData({
        url,
        method: 'delete',
        id,
    })
}

export async function apiGetOldAppointementHistory(id, queryParams = {}) {
    const url = buildURL(`${endpoint}/old-appointment`, [id], queryParams)
    return ApiService.fetchData({
        url: url,
        method: 'get',
    })
}
export async function apiAddOldAppointement(data, queryParams = {}) {
    const url = buildURL(`${endpoint}/old-consultation`, [], queryParams)
    return ApiService.fetchData({
        url,
        method: 'post',
        data,
    })
}

export async function apiEditOldAppointment(id, data, queryParams = {}) {
    const url = buildURL(`${endpoint}/old-consultation`, [id], queryParams)
    return ApiService.fetchData({
        url,
        method: 'patch',
        data,
    })
}
